import {
  calculateCltVsPj, calculateEmployerCost, calculatePlr, calculateTermination, calculateThirteenth, calculateVacation,
  type Competence, type TerminationType,
} from '@amigodoclt/core';

export type Calculator = 'ferias' | 'decimo-terceiro' | 'rescisao' | 'plr' | 'clt-pj' | 'custo-empregador';
export type FieldKind = 'money' | 'range' | 'stepper' | 'percent';
export type Field = { key: string; label: string; initial: number; kind: FieldKind; min?: number; max?: number; step?: number };
export type Toggle = { key: string; label: string; icon: string };
export type Values = Record<string, number>;
export type Flags = Record<string, boolean>;

export type CalculatorEntry = {
  title: string;
  intro: string;
  icon: string;
  /** Rotulo do bruto no card de resultado; cada calculadora tem a sua base. */
  grossLabel?: string;
  /** Lê o salário-base do formulário compartilhado em vez de ter campo próprio. */
  usesSalaryBase?: boolean;
  fields: Field[];
  toggles?: Toggle[];
  run: (values: Values, competence: Competence, flags: Flags) => unknown;
};

export const calculators: Record<Calculator, CalculatorEntry> = {
  ferias: {
    title: 'Férias', icon: 'beach_access', grossLabel: 'Férias brutas', usesSalaryBase: true,
    intro: 'Simule dias, terço constitucional, abono e adiantamento do 13º.',
    fields: [
      { key: 'days', label: 'Dias de férias', initial: 30, kind: 'range', min: 1, max: 30, step: 1 },
      { key: 'soldDays', label: 'Dias vendidos (abono)', initial: 0, kind: 'stepper', min: 0, max: 10, step: 1 },
      { key: 'dependents', label: 'Dependentes', initial: 0, kind: 'stepper', min: 0, step: 1 },
    ],
    toggles: [{ key: 'advanceThirteenth', label: 'Adiantar 1ª parcela do 13º nas férias', icon: 'redeem' }],
    run: (v, c, f) => calculateVacation({ competence: c, monthlySalary: v.monthlySalary ?? 0, days: v.days, soldDays: v.soldDays, dependents: v.dependents, advanceThirteenth: f.advanceThirteenth ?? false }),
  },
  'decimo-terceiro': {
    title: '13º salário', icon: 'redeem', grossLabel: '13º bruto', usesSalaryBase: true,
    intro: 'Calcule avos, médias, parcelas e descontos em separado.',
    fields: [
      { key: 'variableAverage', label: 'Média mensal de variáveis', initial: 0, kind: 'money', min: 0 },
      { key: 'monthsEarned', label: 'Avos adquiridos', initial: 12, kind: 'range', min: 0, max: 12, step: 1 },
      { key: 'firstInstallmentPaid', label: '1ª parcela já recebida', initial: 2500, kind: 'money', min: 0 },
    ],
    run: (v, c) => calculateThirteenth({ competence: c, monthlySalary: v.monthlySalary ?? 0, variableAverage: v.variableAverage, monthsEarned: v.monthsEarned, firstInstallmentPaid: v.firstInstallmentPaid }),
  },
  rescisao: {
    title: 'Rescisão', icon: 'logout', grossLabel: 'Rescisão bruta', usesSalaryBase: true,
    intro: 'Estimativa para desligamento; confirme sempre o TRCT e a convenção coletiva.',
    fields: [
      { key: 'workedDays', label: 'Dias trabalhados no mês', initial: 15, kind: 'range', min: 0, max: 31, step: 1 },
      { key: 'proportionalVacationMonths', label: 'Avos de férias proporcionais', initial: 6, kind: 'range', min: 0, max: 12, step: 1 },
      { key: 'thirteenthMonths', label: 'Avos de 13º', initial: 6, kind: 'range', min: 0, max: 12, step: 1 },
      { key: 'fgtsBalance', label: 'Saldo FGTS informado', initial: 10000, kind: 'money', min: 0 },
    ],
    run: (v, c) => calculateTermination({ competence: c, monthlySalary: v.monthlySalary ?? 0, type: 'without-cause', workedDays: v.workedDays, proportionalVacationMonths: v.proportionalVacationMonths, thirteenthMonths: v.thirteenthMonths, fgtsBalance: v.fgtsBalance }),
  },
  plr: {
    title: 'PLR', icon: 'trending_up',
    intro: 'A tabela de IR é exclusiva e considera as parcelas já recebidas no ano.',
    fields: [
      { key: 'annualPlr', label: 'PLR anual total', initial: 12000, kind: 'money', min: 0 },
      { key: 'priorInstallments', label: 'Parcelas anteriores', initial: 0, kind: 'money', min: 0 },
    ],
    run: (v, c) => calculatePlr({ competence: c, annualPlr: v.annualPlr ?? 0, priorInstallments: v.priorInstallments }),
  },
  'clt-pj': {
    title: 'CLT × PJ', icon: 'compare_arrows',
    intro: 'Compare caixa, reservas e patrimônio diferido com premissas explícitas.',
    fields: [
      { key: 'cltMonthlyNet', label: 'CLT líquido mensal', initial: 4000, kind: 'money', min: 0 },
      { key: 'fgtsMonthly', label: 'FGTS mensal', initial: 400, kind: 'money', min: 0 },
      { key: 'monthlyBenefits', label: 'Benefícios mensais', initial: 500, kind: 'money', min: 0 },
      { key: 'pjMonthlyInvoice', label: 'Faturamento PJ mensal', initial: 8000, kind: 'money', min: 0 },
      { key: 'pjEffectiveTaxPercent', label: 'Imposto efetivo PJ (%)', initial: 10, kind: 'percent', min: 0, max: 100 },
      { key: 'pjBilledMonths', label: 'Meses faturados', initial: 12, kind: 'range', min: 0, max: 12, step: 1 },
      { key: 'monthlyExpenses', label: 'Despesas PJ mensais', initial: 300, kind: 'money', min: 0 },
    ],
    run: (v, c) => calculateCltVsPj({ competence: c, cltMonthlyNet: v.cltMonthlyNet ?? 0, fgtsMonthly: v.fgtsMonthly, monthlyBenefits: v.monthlyBenefits, pjMonthlyInvoice: v.pjMonthlyInvoice ?? 0, pjEffectiveTaxPercent: v.pjEffectiveTaxPercent ?? 0, pjBilledMonths: v.pjBilledMonths, monthlyExpenses: v.monthlyExpenses }),
  },
  'custo-empregador': {
    title: 'Custo do empregador', icon: 'corporate_fare', usesSalaryBase: true,
    intro: 'Veja salário, benefícios, provisões e encargos editáveis.',
    fields: [
      { key: 'monthlyBenefits', label: 'Benefícios mensais', initial: 500, kind: 'money', min: 0 },
      { key: 'annualPlr', label: 'PLR anual', initial: 1000, kind: 'money', min: 0 },
      { key: 'fgtsPercent', label: 'FGTS (%)', initial: 8, kind: 'percent', min: 0, max: 100 },
      { key: 'cppPercent', label: 'CPP (%)', initial: 20, kind: 'percent', min: 0, max: 100 },
      { key: 'ratFapPercent', label: 'RAT/FAP (%)', initial: 2, kind: 'percent', min: 0, max: 100 },
      { key: 'thirdPartiesPercent', label: 'Terceiros (%)', initial: 5.8, kind: 'percent', min: 0, max: 100 },
    ],
    run: (v, c) => calculateEmployerCost({ competence: c, monthlySalary: v.monthlySalary ?? 0, monthlyBenefits: v.monthlyBenefits, annualPlr: v.annualPlr, fgtsPercent: v.fgtsPercent, cppPercent: v.cppPercent, ratFapPercent: v.ratFapPercent, thirdPartiesPercent: v.thirdPartiesPercent }),
  },
};

export const terminationTypes: { value: TerminationType; label: string }[] = [
  { value: 'resignation', label: 'Pedido de demissão' },
  { value: 'without-cause', label: 'Sem justa causa' },
  { value: 'agreement', label: 'Acordo entre as partes' },
  { value: 'just-cause', label: 'Justa causa' },
];

/** Grade do hub, na ordem do design. */
export const tools: { to: string; title: string; icon: string; description: string }[] = [
  { to: '/calculos/salario', title: 'Salário líquido', icon: 'payments', description: 'Bônus, horas extras, descontos e quinzenal.' },
  { to: '/calculos/ferias', title: 'Férias', icon: 'beach_access', description: calculators.ferias.intro },
  { to: '/calculos/decimo-terceiro', title: '13º salário', icon: 'redeem', description: calculators['decimo-terceiro'].intro },
  { to: '/calculos/plr', title: 'PLR', icon: 'trending_up', description: calculators.plr.intro },
  { to: '/calculos/rescisao', title: 'Rescisão', icon: 'logout', description: calculators.rescisao.intro },
  { to: '/calculos/clt-pj', title: 'CLT × PJ', icon: 'compare_arrows', description: calculators['clt-pj'].intro },
  { to: '/comparar', title: 'Comparar cenários', icon: 'balance', description: 'Dois salários sob as mesmas premissas.' },
  { to: '/calculos/custo-empregador', title: 'Custo do empregador', icon: 'corporate_fare', description: calculators['custo-empregador'].intro },
  { to: '/graficos', title: 'Gráficos', icon: 'bar_chart', description: 'Composição do salário e projeção anual.' },
  { to: '/holerite', title: 'Raio-X do holerite', icon: 'document_scanner', description: 'Leia o PDF em memória, sem guardar nada.' },
];

export const overtimePresets = [
  { label: 'HE 50%', premiumPercent: 50 },
  { label: 'HE 75%', premiumPercent: 75 },
  { label: 'HE 100%', premiumPercent: 100 },
];

export const deductionPresets = [
  { label: 'Plano de saúde', icon: 'medical_services' },
  { label: 'Plano odontológico', icon: 'dentistry' },
  { label: 'Vale-refeição', icon: 'restaurant' },
  { label: 'Adiantamento', icon: 'schedule' },
  { label: 'Empréstimo consignado', icon: 'credit_card' },
  { label: 'Contribuição sindical', icon: 'groups' },
];

export const family = [
  { name: 'Cosmo', icon: 'local_florist', color: '#ff7fb8', tint: 'rgba(178,58,117,.28)', url: 'https://cosmo.brandnexx.com/', host: 'cosmo.brandnexx.com', tag: 'Revenda', short: 'Vendas, estoque e metas para revendedoras.', description: 'Vendas, estoque, clientes, financeiro e metas para quem revende Avon, Natura, Mary Kay e O Boticário — com importação da nota da marca e lembretes no WhatsApp.' },
  { name: 'Aero', icon: 'account_balance_wallet', color: '#6fc3ff', tint: 'rgba(58,140,220,.26)', url: 'https://aerocash.brandnexx.com/', host: 'aerocash.brandnexx.com', tag: 'Finanças', short: 'Receitas, despesas e saldos num lugar só.', description: 'Suas receitas, despesas e saldos em um só lugar, leve e rápido — para saber onde o dinheiro entra, para onde vai e quanto sobra no fim do mês.' },
  { name: 'Chronocube', icon: 'view_timeline', color: '#a99bff', tint: 'rgba(120,100,220,.28)', url: 'https://chronocube.brandnexx.com/', host: 'chronocube.brandnexx.com', tag: 'Arquivos', short: 'Seus arquivos numa linha do tempo navegável.', description: 'Guarda seus arquivos em uma linha do tempo navegável: o tronco é tudo que você tem, os galhos são as categorias e cada bloco é um arquivo, na data em que chegou.' },
];
